<template>
  <admin-wrapper
    :has-buttons="false"
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
  >
    <template #datasource.input="data">
      <v-col cols="4">
        <data-input v-bind="data" v-model="dataSourceType"> </data-input>
      </v-col>
    </template>
    <template #added.input>
      <added-file-info :file="fileName" :date="date" @delete="deleteNmtHandler" :disabled="!isModuleEnabled">
        <template #prependIcon="{disabled}">
          <v-tooltip bottom color="font">
            <template #activator="{ on }">
              <v-icon :color="disabled ? 'disabled' : 'primary'" class="pr-2" v-on="on">
                mdi-information
              </v-icon>
            </template>
            {{ info }}
          </v-tooltip>
        </template>
      </added-file-info>
    </template>
    <template #file.input="{dropzoneOptions}">
      <file-input
        @successfullyAdded="init"
        :disabled="!isModuleEnabled"
        type="theme_picture"
        :options="dropzoneOptions"
      ></file-input>
    </template>
    <template #map.input>
      <v-col cols="4">
        <nmt-settings-map @fetched="init"> </nmt-settings-map>
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import FileInput from '@/components/FileInput';
import DataInput from '@/components/DataInput';
import NmtSettingsMap from '@/components/NmtSettingsMap';
import AddedFileInfo from '@/components/AddedFileInfo';

export default {
  name: 'NmtSettings',
  components: {
    FileInput,
    AdminWrapper,
    DataInput,
    NmtSettingsMap,
    AddedFileInfo,
  },
  data: vm => ({
    dataSourceType: 'GeoTIFF',
    isFormFetched: false,
    isNmtUploaded: false,
    fileName: undefined,
    date: undefined,
    source: undefined,
    info: vm.$i18n.t('button.nmtInfo'),
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.DEM.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@dem_module_enabled'),
    dataBaseEpsg: get('map/dataBaseEpsg'),
    inputs() {
      const basic = [
        ...[
          this.isNmtUploaded
            ? {
                name: 'added',
                translationPath: 'admin.datasource',
                subtitle:
                  this.source === 'wcs'
                    ? 'admin.uploadedByUCS'
                    : this.source === 'upload'
                    ? 'admin.uploadedByFile'
                    : '',
                align: 'start',
                isCustom: true,
              }
            : this.dataSourceType === 'GeoTIFF'
            ? {
                name: 'file',
                translationPath: 'admin.datasource',
                subtitle: `${this.$i18n.t('admin.requiredGeoTIFF')}\n${this.$i18n.t('admin.requiredEPSG', {
                  epsg: this.dataBaseEpsg.text,
                })}`,
                align: 'start',
                isCustom: true,
                subtitleParams: {
                  width: 800,
                  height: 1080,
                },
                dropzoneOptions: {
                  url: `${process.env.VUE_APP_API_URL}/dem/upload`,
                  method: 'post',
                  paramName: 'file',
                  acceptedFiles: '.tiff,.tif',
                  maxFiles: 1,
                },
              }
            : {
                name: 'map',
                translationPath: 'admin.datasource',
                subtitle: 'admin.setLayerBbox',
                align: 'start',
                isCustom: true,
              },
        ],
      ];
      if (!this.isNmtUploaded) {
        basic.splice(1, 0, {
          name: 'datasource',
          translationPath: `admin.dataSourceType`,
          dataType: {
            name: 'select',
          },
          items: ['GeoTIFF', 'GUGIK WCS'],
        });
      }
      return basic;
    },
    moduleEnabler() {
      return {
        name: 'dem_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
  },
  methods: {
    deleteNmt: call('nmt/deleteNmt'),
    getNmtMetadata: call('nmt/getNmtMetadata'),
    async deleteNmtHandler() {
      try {
        await this.deleteNmt();
        this.isNmtUploaded = false;
      } catch (error) {
        console.log(error);
      }
    },
    async init() {
      try {
        const r = await this.getNmtMetadata();
        const { data } = r.data;
        const isNmtUploaded = Object.keys(data || {}).length > 0;
        if (isNmtUploaded) {
          const { acquisition_date, acquisition_source, geoserver_file_path } = data;
          this.date = acquisition_date;
          this.source = acquisition_source;
          this.fileName = geoserver_file_path;
          this.isNmtUploaded = true;
        }
        this.isFormFetched = true;
      } catch (error) {
        console.log(error);
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep .text-caption {
  white-space: break-spaces;
}
</style>
